import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ListChecks, MoreHorizontal, Pencil, Trash2 } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/admin/_common/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/admin/_common/ui/dropdown-menu";
import { Skeleton } from "@/app/admin/_common/ui/skeleton";
import TableEmpty from "@/app/admin/_common/TableEmpty";
import ConfirmDialog from "@/app/admin/_common/ConfirmDialog";
import { formatUtcDate } from "@/app/_utils/dayjs";
import { cn } from "@/app/_utils/cn";
import type { ChecklistTemplate } from "@/app/_services/ChecklistService";

function fieldCount(template: ChecklistTemplate) {
  return template.schema.categories.reduce(
    (sum, c) => sum + c.sections.reduce((n, s) => n + s.fields.length, 0),
    0
  );
}

export default function ChecklistTemplateList({
  templates,
  isLoading,
  onDelete,
}: {
  templates: ChecklistTemplate[];
  isLoading: boolean;
  onDelete: (template: ChecklistTemplate) => void;
}) {
  const [pendingDelete, setPendingDelete] = useState<ChecklistTemplate | null>(null);
  const navigate = useNavigate();

  const open = (template: ChecklistTemplate) =>
    navigate(`/admin/tools/checklists/${template.id}`);

  const actionsMenu = (template: ChecklistTemplate) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="ml-auto flex size-6 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
        >
          <MoreHorizontal className="size-4 shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => open(template)}>
          <Pencil className="mr-2 size-4" />
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-red-600 focus:bg-red-50 focus:text-red-700"
          onClick={() => setPendingDelete(template)}
        >
          <Trash2 className="mr-2 size-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const isEmpty = !isLoading && templates.length === 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-t border-border bg-card md:rounded-md md:border md:border-border">
      <Table
        containerClassName="min-h-0 flex-1 overflow-x-auto overflow-y-auto"
        className={cn("min-w-[720px] table-fixed", isEmpty && "h-full")}
      >
        <TableHeader>
          <TableRow className="sticky top-0 z-10 hover:bg-muted [&>th]:bg-muted">
            <TableHead className="h-8 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground w-[24%]">Name</TableHead>
            <TableHead className="h-8 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Description</TableHead>
            <TableHead className="h-8 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground w-24 text-right">Categories</TableHead>
            <TableHead className="h-8 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground w-20 text-right">Fields</TableHead>
            <TableHead className="h-8 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground w-[14%]">Updated</TableHead>
            <TableHead className="h-8 w-12 pr-6 text-right" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <TableRow key={i} className="hover:bg-transparent">
                <TableCell className="py-2"><Skeleton className="h-3.5 w-32" /></TableCell>
                <TableCell className="py-2"><Skeleton className="h-3 w-56" /></TableCell>
                <TableCell className="py-2"><Skeleton className="ml-auto h-3 w-6" /></TableCell>
                <TableCell className="py-2"><Skeleton className="ml-auto h-3 w-8" /></TableCell>
                <TableCell className="py-2"><Skeleton className="h-3 w-20" /></TableCell>
                <TableCell className="py-2 pr-6"><Skeleton className="ml-auto size-4 rounded" /></TableCell>
              </TableRow>
            ))
          ) : isEmpty ? (
            <TableRow className="h-full hover:bg-transparent">
              <TableCell colSpan={6} className="h-full p-0">
                <TableEmpty icon={ListChecks} text="No checklist templates yet" />
              </TableCell>
            </TableRow>
          ) : (
            templates.map((template) => (
              <TableRow key={template.id}>
                <TableCell className="py-1.5">
                  <span
                    className="cursor-pointer truncate text-[13px] text-foreground hover:underline"
                    onClick={() => open(template)}
                  >
                    {template.name}
                  </span>
                </TableCell>
                <TableCell className="truncate py-1.5 text-[12.5px] text-muted-foreground">
                  {template.description || "-"}
                </TableCell>
                <TableCell className="whitespace-nowrap py-1.5 text-right text-[12.5px] text-muted-foreground">
                  {template.schema.categories.length}
                </TableCell>
                <TableCell className="whitespace-nowrap py-1.5 text-right text-[12.5px] text-muted-foreground">
                  {fieldCount(template)}
                </TableCell>
                <TableCell className="whitespace-nowrap py-1.5 text-[12.5px] text-muted-foreground">
                  {formatUtcDate(template.updated_at)}
                </TableCell>
                <TableCell className="py-1.5 pr-6 text-right">{actionsMenu(template)}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => pendingDelete && onDelete(pendingDelete)}
        title="Delete template"
        message="Shows that already use this checklist keep their copy. Are you sure?"
        confirmText="Delete"
      />
    </div>
  );
}
